import React, { useState, useRef, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Stepper,
  Step,
  StepButton,
  Button,
  Container,
  Snackbar
} from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import { Send as SendIcon } from "@material-ui/icons";

import CreativeList from "../CreativeList/CreativeList";
import { assignCreatives } from "../../services/dcm/creatives";
import BackupStep from "../AssetUploader/BackupStep/BackupStep";
import AssetOptions from "../AssetUploader/OptionsStep/AssetOptions";
import AssetReview from "../AssetUploader/ReviewStep/AssetReview";
import AssetNaming from "../AssetUploader/NamingStep/AssetNaming";
import Confirmation from "../Shared/Confirmation/Confirmation";

import { useSelector, useDispatch } from "react-redux";

import { addDcmCreatives, removeCreative } from "../../actions/app/creatives";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%"
  },
  backButton: {
    marginRight: theme.spacing(1)
  },
  instructions: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  stepper: {
    margin: "20px auto 30px",
    width: "60%",
    paddingTop: 0
  }
}));

const next = {
  float: "right"
};

function getSteps() {
  return ["Choose creative(s)", "Select options", "Review"];
}

const creativeTypes = [
  "RICH_MEDIA_DISPLAY_BANNER",
  "RICH_MEDIA_DISPLAY_EXPANDING",
  "RICH_MEDIA_DISPLAY_INTERSTITIAL"
];

function getStepContent(stepIndex, props) {
  switch (stepIndex) {
    case 0:
      return (
        <div>
          <CreativeList
            creativeTypes={creativeTypes}
            selectedCreatives={props.selectedCreatives}
            setSelectedCreatives={props.setSelectedCreatives}
            placements={props.placements}
            ref={(r) => (props.stepRef.current = r)}
          />
        </div>
      );
    case 1:
      return (
        <div>
          <AssetOptions
            isClickEnabled={false}
            isDragDisabled={false}
            isThumbnailEnabled={false}
            isDTKEnabled={true}
            ref={(r) => (props.stepRef.current = r)}
          />
        </div>
      );
    case 2:
      return (
        <div>
          <AssetReview
            isClickEnabled={false}
            isDragDisabled={true}
            isThumbnailEnabled={false}
            ref={(r) => (props.stepRef.current = r)}
          />
        </div>
      );
    default:
      return " ";
  }
}

export default () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [activeStep, setActiveStep] = React.useState(0);
  // Tracks an actual submission. Deriving "done" from
  // activeStep === steps.length is unsafe when the step count is derived
  // from creative state: if a step disappears while the user sits on a
  // later one, activeStep can land on steps.length and report a
  // successful submission that never happened.
  const [hasSubmitted, setHasSubmitted] = React.useState(false);
  const [completed, setCompleted] = React.useState(new Set());
  const [skipped, setSkipped] = React.useState(new Set());
  const [selectedCreatives, setSelectedCreatives] = React.useState([]);
  const bartCreatives = useSelector((state) => state.creatives);
  const steps = getSteps();

  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const stepRef = useRef();

  const history = useHistory();

  useEffect(() => {
    if (bartCreatives.assignedPlacements.length < 1) {
      history.push("/");
    }
  }, [bartCreatives.assignedPlacements.length, history]);

  const validate = () => {
    if (
      stepRef &&
      stepRef.current &&
      stepRef.current.hasOwnProperty("validate")
    ) {
      const validation = stepRef.current.validate();

      if (validation.hasErrors) {
        if (validation.message) {
          setSnackbarMessage(validation.message);
          setSnackbarOpen(true);
        }

        return false;
      }
    }

    return true;
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setHasSubmitted(false);
    setActiveStep(0);
  };

  const handleStep = (step) => () => {
    // The Stepper header stays mounted on the Confirmation screen, so a
    // StepButton click has to drop out of it — otherwise activeStep moves
    // but Confirmation keeps rendering and the user is stuck there.
    setHasSubmitted(false);
    setActiveStep(step);
  };

  const isStepSkipped = (step) => {
    return skipped.has(step);
  };

  function isStepComplete(step) {
    return completed.has(step);
  }

  const determineNextFunction = () => {
    switch (activeStep) {
      case 0:
        return () => {
          if (!validate()) {
            return;
          }

          let selectedCreativeIds = selectedCreatives.map((c) => c.id);

          let dcmIdsInBart = Object.values(bartCreatives.byId).map(
            (bartCreatives) => bartCreatives.dcmId
          );

          let creativesToAdd = selectedCreatives.filter(
            (dcmCreative) => !dcmIdsInBart.includes(dcmCreative.id)
          );

          let creativesToRemove = Object.values(bartCreatives.byId).filter(
            (bartCreative) => !selectedCreativeIds.includes(bartCreative.dcmId)
          );

          for (let i = 0; i < creativesToRemove.length; i++) {
            dispatch(removeCreative(creativesToRemove[i].id));
          }

          dispatch(addDcmCreatives(creativesToAdd));

          handleNext();
        };
      default:
        return () => {
          if (!validate()) {
            return;
          }

          handleNext();
        };
    }
  };

  const handleTraffic = () => {
    assignCreatives(
      Object.values(bartCreatives.byId),
      bartCreatives.assignedPlacements,
      bartCreatives.rotationType,
      bartCreatives.weightDistribution
    );
    // Set before advancing: the Confirmation screen is gated on this,
    // not on the step index.
    setHasSubmitted(true);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  return (
    <div className={classes.root}>
      <Stepper
        alternativeLabel
        activeStep={activeStep}
        nonLinear
        className={classes.stepper}
      >
        {steps.map((label, index) => {
          const stepProps = {};
          const buttonProps = {};
          if (isStepSkipped(index)) {
            stepProps.completed = false;
          }
          return (
            <Step key={label} {...stepProps}>
              <StepButton
                onClick={handleStep(index)}
                completed={isStepComplete(index)}
                {...buttonProps}
              >
                {label}
              </StepButton>
            </Step>
          );
        })}
      </Stepper>
      <Container maxWidth="lg">
        <div>
          {hasSubmitted ? (
            <Confirmation handleResetParent={handleReset} />
          ) : (
            <div>
              <div style={next}>
                <Button
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  className={classes.button}
                  disableElevation
                >
                  Back
                </Button>
                {activeStep === steps.length - 1 ? (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleTraffic}
                    startIcon={<SendIcon />}
                    disableElevation
                  >
                    Submit Placements
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={determineNextFunction()}
                    disableElevation
                  >
                    Next
                  </Button>
                )}
              </div>
              {getStepContent(activeStep, {
                stepRef,
                selectedCreatives,
                setSelectedCreatives,
                placements: bartCreatives.assignedPlacements
              })}
            </div>
          )}
        </div>
      </Container>
      <Snackbar
        autoHideDuration={3000}
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
      >
        <MuiAlert onClose={() => setSnackbarOpen(false)} severity="warning">
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  );
};
